import { Swiper } from '@theme/swiper';

class ProductRecommendationsComponent extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Ignore when the data-algolia-loaded attribute changes
      if (mutation.attributeName === 'data-algolia-loaded') continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    // Recommendation type: collection
    if (this.dataset.collectionId) {
      this.initialiseCarousel();

      // Recommendation type: using Algolia's recommendations
    } else if (this.dataset.algoliaRelated) {
      this.#intersectionObserver.observe(this);
      this.#mutationObserver.observe(this, { attributes: true });

      // Recommendation type: related or complementary,
      // using Shopify's Product Recommendations API
    } else {
      this.#intersectionObserver.observe(this);
      this.#mutationObserver.observe(this, { attributes: true });
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, algoliaRelated } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // Use Algolia recommendations if specified
    if (algoliaRelated === 'true') {
      // Don't reload if already loaded
      if (this.dataset.algoliaLoaded === 'true') {
        return;
      }
      this.#loadAlgoliaRecommendations();
      return;
    }

    const { recommendationsPerformed, sectionId, intent } = this.dataset;

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(
          `product-recommendations-component[id="${id}"]`
        );

        if (
          recommendations?.innerHTML &&
          recommendations.innerHTML.trim().length
        ) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;

          this.initialiseCarousel();

          this.initialiseReviewStars();

          this.initialiseWishlistButtons();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Load Algolia recommendations using the Recommendations API
   */
  async #loadAlgoliaRecommendations() {
    const {
      algoliaAppId,
      algoliaApiKey,
      algoliaIndex,
      variantId,
      productsLimit,
    } = this.dataset;

    if (!algoliaAppId || !algoliaApiKey || !algoliaIndex) {
      this.#handleError(new Error('Missing required Algolia configuration'));
      return;
    }

    if (!variantId) {
      this.#handleError(
        new Error('Missing variant ID for Algolia recommendations')
      );
      return;
    }

    const objectID = variantId;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const requestBody = {
        requests: [
          {
            indexName: algoliaIndex,
            model: 'related-products',
            objectID: objectID,
            threshold: 0,
            maxRecommendations: parseInt(productsLimit || '8') || 8,
          },
        ],
      };

      const response = await fetch(
        `https://${algoliaAppId}.algolia.net/1/indexes/*/recommendations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Algolia-Application-Id': algoliaAppId,
            'X-Algolia-API-Key': algoliaApiKey,
          },
          body: JSON.stringify(requestBody),
          signal: this.#activeFetch.signal,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Algolia API Error Response:', errorText);
        throw new Error(
          `Algolia API returned ${response.status}: ${errorText}`
        );
      }

      const data = await response.json();

      const recommendations = data.results?.[0]?.hits || [];

      this.classList.remove('is-hidden');
      if (recommendations.length === 0) {
        console.warn(
          'No Algolia recommendations available for this product. This could mean:',
          '\n1. The Related Products model needs to be trained in Algolia',
          '\n2. There are no related products for this item'
        );
        this.classList.add('is-hidden');
        this.dataset.algoliaLoaded = 'true';
        return;
      }

      this.#renderAlgoliaRecommendations(recommendations);
      this.dataset.algoliaLoaded = 'true';
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Render Algolia recommendations as product cards
   * @param {any[]} recommendations
   */
  #renderAlgoliaRecommendations(recommendations) {
    const productsContainer = this.querySelector('.swiper-wrapper');
    if (!productsContainer) return;

    productsContainer.innerHTML = '';

    for (const hit of recommendations) {
      const productCard = this.#createProductCard(hit);
      productsContainer.appendChild(productCard);
    }

    this.initialiseCarousel();
    this.initialiseReviewStars();
    this.initialiseWishlistButtons();
  }

  /**
   * Create a product card element from Algolia hit
   * @param {any} hit
   * @returns {HTMLElement}
   */
  #createProductCard(hit) {
    const slide = document.createElement('div');
    slide.className = 'product-recommendations__product swiper-slide';

    const productUrl = `/products/${hit.handle}`;
    const imageUrl = hit.image || hit.images?.[0] || '';

    const hasDiscount =
      hit.compare_at_price && hit.compare_at_price > hit.price;
    const discountPercentage = hasDiscount
      ? Math.floor(
          ((hit.compare_at_price - hit.price) / hit.compare_at_price) * 100
        )
      : 0;

    // Backorder / preorder
    const isBackorder =
      hit.inventory_available &&
      hit.inventory_quantity <= 0 &&
      hit.inventory_policy === 'continue' &&
      hit.meta?.custom?.availability_type === 'backorder' &&
      hit.meta?.custom?.order_lead_time !== '';
    const isPreorder =
      hit.inventory_available &&
      hit.inventory_quantity <= 0 &&
      hit.inventory_policy === 'continue' &&
      hit.meta?.custom?.availability_type === 'preorder' &&
      hit.meta?.custom?.date_available !== '';

    slide.innerHTML = `
      <div class="product-card">
        <div class="product-card__inner">
          <a class="image product-card__image" href="${productUrl}">
            <img
              src="${imageUrl}"
              alt="${hit.title || ''}"
              width="100%"
              height="100%"
            />
          </a>

          ${
            hit.tags && hit.tags.length > 0
              ? `
            <div class="product-card__tags">
              ${hit.tags
                .filter((/** @type {string} */ tag) => tag.startsWith('Badge_'))
                .map(
                  (/** @type {string} */ tag) =>
                    `<span class="product-card__tag tag">${tag.replace(
                      'Badge_',
                      ''
                    )}</span>`
                )
                .join('')}
            </div>
          `
              : ''
          }

          <a class="product-card__title p is-small is-bold" href="${productUrl}">
            ${hit.title || ''}
          </a>

          ${
            hit.meta?.custom?.author_illustrator_artist_etc
              ? `
            <div class="product-card__author-publisher">
              ${parseAuthors(hit.meta.custom.author_illustrator_artist_etc).map((/** @type {string} */ author) => `<a href="/search?q=${author}" class="p is-small">${author}</a>`).join(', ')}
            </div>
          `
              : hit.meta?.custom?.publisher
              ? `
            <a href="/search?q=${hit.meta.custom.publisher}" class="product-card__author-publisher p is-small">
              ${hit.meta.custom.publisher}
            </a>
          `
              : ''
          }

          <div class="yotpo-widget-instance" data-yotpo-instance-id="1191105" data-yotpo-product-id="${
            hit.id
          }" data-yotpo-section-id="index"></div>

          ${
            hit.meta?.custom?.format
              ? `
            <span class="product-card__format p is-small">${hit.meta.custom.format}</span>
          `
              : ''
          }

          <div class="product-pricing product-card__pricing p is-large">
            ${
              hasDiscount
                ? `<p class="product-pricing__compare" data-compare-price="${Math.round(
                    hit.compare_at_price * 100
                  )}">
                $${hit.compare_at_price.toFixed(2)}
              </p>`
                : ''
            }
            <p class="product-pricing__price" data-price="${Math.round(
              hit.price * 100
            )}">
              $${hit.price.toFixed(2)}
            </p>
          </div>

          ${
            discountPercentage > 0
              ? `
            <span class="product-discount product-card__discount p is-small">
              ${Theme.translations.discount?.replace(
                '{{ percentage }}',
                `${discountPercentage}%`
              )}
            </span>
          `
              : ''
          }
              
          <algolia-product-form-component
            data-product-id="${hit.id}"
            class="product-form__form"
          >
            <div
              class="visually-hidden"
              aria-live="assertive"
              role="status"
              aria-atomic="true"
            ></div>

            <form
              id="product-form-${hit.objectID}"
              action="/cart/add"
              method="post"
              enctype="multipart/form-data"
              data-type="add-to-cart-form"
            >
              <input type="hidden" name="id" value="${hit.objectID}" />
              <input type="hidden" name="quantity" value="1" />

              <button
                data-add-to-cart-button-algolia
                id="addToCartButton-${hit.objectID}"
                type="submit"
                name="add"
                class="add-to-cart-btn product-form__add-button button for-card 
                ${hit.inventory_available ? '' : 'disabled'}"
              >
                <span class="add-to-cart-text">
                  <span class="add-to-cart-text__content">
                    ${
                      isPreorder
                        ? Theme.translations.preorder
                        : isBackorder
                        ? Theme.translations.backorder
                        : hit.inventory_available
                        ? Theme.translations.add_short
                        : Theme.translations.out_of_stock
                    }
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  class="add-to-cart-text--added"
                >
                  <span>${Theme.translations.added}</span>
                </span>
              </button>
            </form>
          </algolia-product-form-component>

          <button
            class="swym-add-to-wishlist-view-product product_${
              hit.id
            } wishlist-add-button product-card__wishlist"
            data-with-epi="true"
            data-swaction="addToWishlist"
            data-product-id="${hit.id}"
            data-variant-id="${hit.objectID}"
            data-product-url="${
              window.shopUrl
            }${window.algoliaShopify.helpers.instantsearchLink(hit)}"
          >
            <svg
              viewBox="0 0 21 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.3626 2.41452C17.9144 1.96607 17.3822 1.61034 16.7965 1.36763C16.2107 1.12492 15.5829 1 14.9488 1C14.3148 1 13.6869 1.12492 13.1012 1.36763C12.5154 1.61034 11.9832 1.96607 11.535 2.41452L10.6048 3.34476L9.67453 2.41452C8.76913 1.50912 7.54115 1.00047 6.26072 1.00047C4.98029 1.00047 3.75231 1.50912 2.84691 2.41452C1.94151 3.31992 1.43286 4.5479 1.43286 5.82833C1.43286 7.10875 1.94151 8.33674 2.84691 9.24214L10.6048 17L18.3626 9.24214C18.8111 8.79391 19.1668 8.26171 19.4095 7.67596C19.6522 7.0902 19.7772 6.46237 19.7772 5.82833C19.7772 5.19428 19.6522 4.56645 19.4095 3.9807C19.1668 3.39494 18.8111 2.86275 18.3626 2.41452Z"
                stroke="#282828"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    `;

    return slide;
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('is-hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  initialiseCarousel() {
    const carousel = this.querySelector('[data-carousel]');
    if (!carousel) return;

    const swiperConfig = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesOffsetBefore: 24,
      slidesOffsetAfter: 24,
      allowTouchMove: true,
      effect: 'slide',
      speed: 400,
      grabCursor: true,
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      },
      breakpoints: {
        990: {
          slidesOffsetBefore: 64,
          slidesOffsetAfter: 64,
        },
      },
    };

    new Swiper(carousel, swiperConfig);
  }

  initialiseReviewStars() {
    if (typeof window.yotpoWidgetsContainer?.initWidgets === 'function') {
      window.yotpoWidgetsContainer.initWidgets();
    }
  }

  initialiseWishlistButtons() {
    window._swat?.initializeActionButtons(
      `product-recommendations-component#${this.id}`
    );
  }
}

/**
 * Safely parses the author field, handling both JSON strings and plain strings
 * @param {string} value
 * @returns {string[]}
 */
const parseAuthors = (value) => {
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    return [value];
  }
};

if (!customElements.get('product-recommendations-component')) {
  customElements.define(
    'product-recommendations-component',
    ProductRecommendationsComponent
  );
}
